<template>
  <MenuContainer is-enterprise :menu-items="menuItems" dashboardLink="/enterprise-groups"
    accountLink="/enterprise-account">
    <slot />
  </MenuContainer>
</template>

<script setup>
import MenuContainer from "../components/MenuContainer.vue";
import {
  BuildingLibraryIcon,
  // BuildingOffice2Icon,
  CogIcon,
  DocumentDuplicateIcon,
  // KeyIcon,
  UserGroupIcon,
  GlobeAmericasIcon,
  BuildingStorefrontIcon,
  ChartBarIcon,
} from "@heroicons/vue/24/outline";
import {
  BuildingLibraryIcon as BuildingLibraryIconSolid,
  // BuildingOffice2Icon as BuildingOffice2IconSolid,
  CogIcon as CogIconSolid,
  DocumentDuplicateIcon as DocumentDuplicateIconSolid,
  // KeyIcon as KeyIconSolid,
  UserGroupIcon as UserGroupIconSolid,
  GlobeAmericasIcon as GlobeAmericasIconSolid,
  BuildingStorefrontIcon as BuildingStorefrontIconSolid,
  ChartBarIcon as ChartBarIconSolid,
} from "@heroicons/vue/24/solid";
import { computed, provide } from "vue";
import { useAccountStore } from "../stores/AccountStore";
import { ENTERPRISE_OWNER } from "../constants/Roles";
import { getEnterpriseNouns } from "../helpers/EnterpriseNouns";
import { ENTERPRISE_TYPE_CORPORATE_CHAIN, ENTERPRISE_TYPE_STAFFING_AGENCY } from "../constants/EnterpriseTypes";

const accountStore = useAccountStore();

const activeEnterpriseType = computed(() => accountStore.user?.activeEnterprise?.type);
const activeEnterpriseNouns = computed(() => getEnterpriseNouns(activeEnterpriseType.value));

provide("activeEnterpriseNouns", activeEnterpriseNouns);

/**
 * Determine if the user has access to an enterprise feature.
 */
const hasEnterpriseFeature = (featureName) => {
  return accountStore.user?.features?.enterprise?.[featureName];
};

const menuItems = computed(() => {
  let enterpriseGroupIcon;

  switch (activeEnterpriseType.value) {
  case ENTERPRISE_TYPE_CORPORATE_CHAIN:
    enterpriseGroupIcon = {
      icon: GlobeAmericasIcon,
      iconSolid: GlobeAmericasIconSolid,
    };
    break;
  case ENTERPRISE_TYPE_STAFFING_AGENCY:
    enterpriseGroupIcon = {
      icon: UserGroupIcon,
      iconSolid: UserGroupIconSolid,
    };
    break;
  default:
    enterpriseGroupIcon = {
      icon: BuildingStorefrontIcon,
      iconSolid: BuildingStorefrontIconSolid,
    };
  }

  const baseItems = [
    {
      name: activeEnterpriseNouns.value.groupPlural,
      link: "/enterprise-groups",
      icon: enterpriseGroupIcon.icon,
      iconSolid: enterpriseGroupIcon.iconSolid,
    },
    // 5/23/26 - Temp removal until we identify how we want to re-introduce manage group action when ready
    // {
    //   name: activeEnterpriseNouns.value.companyNamePlural,
    //   link: "/enterprise-companies",
    //   icon: BuildingOffice2Icon,
    //   iconSolid: BuildingOffice2IconSolid,
    // },
    // 5/23/26 - Temp removal until we identify how we want to re-introduce manage group action when ready
    // {
    //   name: "Users",
    //   link: "/enterprise-access-manager",
    //   icon: KeyIcon,
    //   iconSolid: KeyIconSolid,
    // },
    {
      name: "Billing",
      link: "/enterprise-billing-groups",
      icon: BuildingLibraryIcon,
      iconSolid: BuildingLibraryIconSolid,
    },
  ];

  if (accountStore.user?.activeEnterprise?.isFranchisor) {
    baseItems.push({
      name: "Templates",
      link: "/enterprise-templates",
      icon: DocumentDuplicateIcon,
      iconSolid: DocumentDuplicateIconSolid,
    });
  }

  if (hasEnterpriseFeature("analytics")) {
    baseItems.push({
      name: "Analytics",
      link: "/enterprise-analytics",
      icon: ChartBarIcon,
      iconSolid: ChartBarIconSolid,
      beta: true,
    });
  }

  if (accountStore.user?.activeEnterprise?.role === ENTERPRISE_OWNER) {
    baseItems.push({
      name: "Settings",
      link: "/enterprise-settings",
      icon: CogIcon,
      iconSolid: CogIconSolid,
    });
  }

  return baseItems;
});

</script>
